/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Storefront, Plus, Pencil, Trash, X, SignOut, Bell,
  Package, Wheelchair, CurrencyInr, Tag, ToggleLeft, ToggleRight,
  ArrowLeft, ShoppingBag, Truck,
} from "@phosphor-icons/react";
import {
  partnerApi, fmtPartnerErr, clearPartnerToken, isPartnerAuthed,
} from "../../lib/partnerApi";

export default function PartnerListings() {
  const nav = useNavigate();
  const [partner, setPartner] = useState(null);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [me, prods] = await Promise.all([
        partnerApi.get("/service-partners/me"),
        partnerApi.get("/service-partners/me/products"),
      ]);
      setPartner(me.data.partner);
      setProducts(prods.data.products || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearPartnerToken();
        nav("/service-partner/login", { replace: true });
      } else toast.error(fmtPartnerErr(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    if (!isPartnerAuthed()) {
      nav("/service-partner/login", { replace: true });
      return;
    }
    load();
  }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await partnerApi.delete(`/service-partners/me/products/${p.id}`);
      toast.success("Product removed");
      load();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  const toggleActive = async (p) => {
    try {
      await partnerApi.put(`/service-partners/me/products/${p.id}`,
                            { is_active: !p.is_active });
      load();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };

  if (loading) return <Loading/>;

  const saleCount = products.filter(p => p.type === "sale").length;
  const rentalCount = products.filter(p => p.type === "rental").length;

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="partner-listings">
      <header className="bg-white border-b border-[#E5E2D9] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/service-partner/dashboard" className="inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:text-[#2D5A27]">
            <ArrowLeft size={11}/> Dashboard
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/service-partner/orders" data-testid="link-orders"
                   className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <ShoppingBag size={12}/> Orders
            </Link>
            <Link to="/service-partner/deliveries"
                   className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <Truck size={12}/> Deliveries
            </Link>
            <button onClick={() => { clearPartnerToken(); nav("/service-partner/login"); }}
                     className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <SignOut size={12}/> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
              {partner?.name} · Catalog
            </div>
            <h1 className="font-heading text-3xl mt-1 text-[#1A1A1A]">Your listings</h1>
            <p className="text-sm text-[#5C5A55] mt-1.5 max-w-xl">
              Add the products or services you offer. Customers browsing your storefront
              will see active listings instantly.
            </p>
          </div>
          <button onClick={() => setCreating(true)} data-testid="btn-new-product"
                   className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
            <Plus size={14} weight="bold"/> Add product
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Stat label="Total listings" value={products.length}/>
          <Stat label="Sale SKUs" value={saleCount} color="#0B2B66"/>
          <Stat label="Rental SKUs" value={rentalCount} color="#C25D38"/>
        </div>

        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Pricing</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-[#8A8780]">
                  Nothing here yet — click "Add product" to start your catalog.
                </td></tr>
              )}
              {products.map((p, i) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`}
                    className={i % 2 ? "bg-[#FAFAF7]" : "bg-white"}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1A1A1A]">{p.name}</div>
                    {p.description && <div className="text-[11px] text-[#5C5A55] truncate max-w-[260px]">{p.description}</div>}
                    {p.sku && <div className="text-[10px] font-mono text-[#8A8780]">SKU: {p.sku}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${
                      p.type === "rental" ? "bg-[#F4C7B9] text-[#7A2E18]" : "bg-[#D4E0D1] text-[#1A3317]"
                    }`}>{p.type}</span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-[#1A1A1A]">
                    {p.type === "sale"
                      ? <span>₹{(p.price ?? 0).toFixed(2)} <span className="text-[10px] text-[#8A8780]">/ {p.unit}</span></span>
                      : <div className="space-y-0.5">
                          {p.daily_rate != null && <div>₹{p.daily_rate}/day</div>}
                          {p.weekly_rate != null && <div className="text-[#5C5A55]">₹{p.weekly_rate}/wk</div>}
                          {p.monthly_rate != null && <div className="text-[#5C5A55]">₹{p.monthly_rate}/mo</div>}
                          {p.deposit != null && <div className="text-[10px] text-[#8A8780]">Deposit ₹{p.deposit}</div>}
                        </div>}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{p.stock ?? "∞"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(p)} data-testid={`toggle-${p.id}`}
                            className={p.is_active ? "text-[#2D5A27]" : "text-[#8A8780]"}>
                      {p.is_active ? <ToggleRight size={20} weight="fill"/> : <ToggleLeft size={20}/>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`}
                            className="h-8 w-8 grid place-items-center text-[#5C5A55] hover:bg-[#F7F6F2] rounded inline-block">
                      <Pencil size={12}/>
                    </button>
                    <button onClick={() => remove(p)} data-testid={`delete-${p.id}`}
                            className="h-8 w-8 grid place-items-center text-[#C25D38] hover:bg-[#FBE7E0] rounded inline-block ml-1">
                      <Trash size={12}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {(creating || editing) && (
        <ProductForm editing={editing}
                       partner={partner}
                       onClose={() => { setCreating(false); setEditing(null); }}
                       onSaved={() => { setCreating(false); setEditing(null); load(); }}/>
      )}
    </div>
  );
}

// ============================================================
function ProductForm({ editing, partner, onClose, onSaved }) {
  const isEdit = !!editing;
  // Default product type: rental for rental partner, sale otherwise.
  const partnerDefaultsRental = partner?.subcategory === "equipment_rental";
  const [form, setForm] = useState(() => ({
    name: editing?.name || "",
    description: editing?.description || "",
    sku: editing?.sku || "",
    type: editing?.type || (partnerDefaultsRental ? "rental" : "sale"),
    unit: editing?.unit || "unit",
    image_url: editing?.image_url || "",
    product_category: editing?.product_category || "",
    pack_info: editing?.pack_info || "",
    price: editing?.price ?? "",
    daily_rate: editing?.daily_rate ?? "",
    weekly_rate: editing?.weekly_rate ?? "",
    monthly_rate: editing?.monthly_rate ?? "",
    deposit: editing?.deposit ?? "",
    stock: editing?.stock ?? "",
    is_active: editing?.is_active ?? true,
  }));
  const [busy, setBusy] = useState(false);
  const isRental = form.type === "rental";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = { ...form };
      ["price","daily_rate","weekly_rate","monthly_rate","deposit","stock"].forEach(k => {
        p[k] = p[k] === "" ? null : Number(p[k]);
      });
      if (isEdit) {
        delete p.type;  // type immutable
        await partnerApi.put(`/service-partners/me/products/${editing.id}`, p);
        toast.success("Product updated");
      } else {
        await partnerApi.post(`/service-partners/me/products`, p);
        toast.success("Product added");
      }
      onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
          onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
             className="bg-white rounded-md w-full max-w-xl my-8 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
              {isEdit ? "Edit product" : "Add product"}
            </div>
            <div className="font-heading text-xl">{isEdit ? form.name : "New listing"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded">
            <X size={16}/>
          </button>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">Listing type</div>
          <div className="flex gap-2">
            <TypeChip on={!isRental && !isEdit} onClick={() => !isEdit && setForm({...form, type:"sale"})}
                       label="Sale" disabled={isEdit}/>
            <TypeChip on={isRental} onClick={() => !isEdit && setForm({...form, type:"rental"})}
                       label="Rental" disabled={isEdit}/>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Product name *">
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    className={inputCls}/>
          </Field>
          <Field label="SKU"><input value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} className={inputCls}/></Field>
          <Field label="Unit" hint="piece, pack, day, hour…">
            <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} className={inputCls}/>
          </Field>
          <Field label="Stock" hint="Leave blank for unlimited">
            <input type="number" min="0" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} className={inputCls}/>
          </Field>
          <Field label="Shop category" hint="Shown as a tab in the customer shop">
            <select value={form.product_category} onChange={e => setForm({...form, product_category: e.target.value})}
                    className={inputCls} data-testid="product-category-select">
              <option value="">— none —</option>
              {[["prescription","Prescription"],["otc","OTC"],["baby_care","Baby Care"],["personal_care","Personal Care"],["wellness","Wellness"],["other","Other"]]
                .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Pack info" hint="e.g. Tablet · 10 pieces">
            <input value={form.pack_info} onChange={e => setForm({...form, pack_info: e.target.value})} className={inputCls} data-testid="product-pack-info"/>
          </Field>
          <div className="col-span-2">
            <Field label="Image URL" hint="Product photo shown in the shop">
              <input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className={inputCls} data-testid="product-image-url"/>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Description">
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
                        className={`${inputCls} h-auto py-2`}/>
            </Field>
          </div>
        </div>

        {!isRental ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price (₹) *">
              <input required type="number" min="0" step="0.01" value={form.price}
                      onChange={e => setForm({...form, price: e.target.value})} className={inputCls}/>
            </Field>
            <div></div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <Field label="Daily rate (₹)">
              <input type="number" min="0" step="0.01" value={form.daily_rate}
                      onChange={e => setForm({...form, daily_rate: e.target.value})} className={inputCls}/>
            </Field>
            <Field label="Weekly rate (₹)">
              <input type="number" min="0" step="0.01" value={form.weekly_rate}
                      onChange={e => setForm({...form, weekly_rate: e.target.value})} className={inputCls}/>
            </Field>
            <Field label="Monthly rate (₹)">
              <input type="number" min="0" step="0.01" value={form.monthly_rate}
                      onChange={e => setForm({...form, monthly_rate: e.target.value})} className={inputCls}/>
            </Field>
            <div className="col-span-2">
              <Field label="Refundable deposit (₹)" hint="Held until item is returned in good condition">
                <input type="number" min="0" step="1" value={form.deposit}
                        onChange={e => setForm({...form, deposit: e.target.value})} className={inputCls}/>
              </Field>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-[#F0ECE3]">
          <label className="inline-flex items-center gap-2 text-xs text-[#5C5A55]">
            <input type="checkbox" checked={form.is_active}
                    onChange={e => setForm({...form, is_active: e.target.checked})}
                    className="accent-[#2D5A27]"/>
            Visible to customers
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="h-10 px-4 rounded border border-[#E5E2D9] text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="product-submit"
                     className="h-10 px-5 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium disabled:opacity-60">
              {busy ? "Saving…" : isEdit ? "Save changes" : "Add product"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27]";
const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-[#8A8780] mt-1">{hint}</div>}
  </label>
);
const TypeChip = ({ on, onClick, label, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled}
           className={`px-3 py-1.5 text-xs rounded border transition ${
             on ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                : "bg-white text-[#5C5A55] border-[#E5E2D9]"
           } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
    {label}
  </button>
);
const Stat = ({ label, value, color }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
    <div className="font-heading text-3xl mt-1" style={{ color: color || "#1A1A1A" }}>{value}</div>
  </div>
);
const Loading = () => (
  <div className="min-h-screen grid place-items-center bg-[#F7F6F2] text-sm text-[#5C5A55]">Loading catalog…</div>
);
